<script setup lang="ts">
import type { WordPaneStatus, WordPromptError } from '@/types/playbookWordAddin';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';

/**
 * One results tab of the Playbooks pane: its action row, the run's progress
 * and failure notices, and either an empty state or the slotted result cards.
 * The count lives on the tab that opens the pane, so the pane itself carries
 * no headline.
 */
defineProps<{
  status: WordPaneStatus;
  /** "3 / 8 evaluations completed"; empty while the pane has nothing to report. */
  progressLabel: string;
  /** Evaluations of this pane's type that failed; listed in their own notice. */
  failures: WordPromptError[];
  failuresTitle: string;
  idleDescription: string;
  emptyDescription: string;
}>();
</script>

<template>
  <n-flex vertical :size="12" :wrap="false" :style="{ minWidth: 0 }">
    <n-flex v-if="$slots.actions" align="center" justify="end" :size="8" :wrap="false">
      <slot name="actions" />
    </n-flex>

    <gaia-banner v-if="status === 'loading' && progressLabel" plain type="info">
      {{ progressLabel }}
    </gaia-banner>

    <gaia-banner v-if="failures.length" type="warning" :title="failuresTitle">
      <n-flex vertical :size="6" :wrap="false">
        <n-flex v-for="failure in failures" :key="failure.id" vertical :size="1" :wrap="false">
          <gaia-text size="dense" weight="semibold" tone="ink">{{ failure.title }}</gaia-text>
          <gaia-text size="dense" tone="text">{{ failure.message }}</gaia-text>
        </n-flex>
      </n-flex>
    </gaia-banner>

    <gaia-empty-state v-if="status === 'idle'" dashed title="Nothing to show yet">
      {{ idleDescription }}
    </gaia-empty-state>

    <gaia-empty-state v-else-if="status === 'empty'" dashed title="No results">
      {{ emptyDescription }}
    </gaia-empty-state>

    <!-- Rendered while loading too: results appear as the run produces them. -->
    <slot v-else />
  </n-flex>
</template>
